import React from 'react';
import { motion } from 'framer-motion';
import { ArcaPlan } from '../../constants/arcaPlans';
import { PlanReturnView } from '../../utils/transferReturns';
import { PensionPlanTransfer } from '../../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import { fmtEur, fmtNum } from '../../utils/formatArcaNumbers';
import toTitleCase from '../../utils/toTitleCase';
import { MetricCard } from 'components/ui/metric-card';
import ContributionSliderV2 from './ContributionSliderV2';
import { Wallet, PiggyBank, TrendingUp, Zap } from 'lucide-react';
import { isNewPlanTransfer } from '../../types/individualDashboard';

interface ActivePlanSectionProps {
	userName: string;
	inviteButton?: React.ReactNode;
	arcaPlan?: ArcaPlan;
	/**
	 * Nombre del plan que el usuario está viendo. Manda sobre `arcaPlan.name`.
	 *
	 * 🔴 `arcaPlan` lo deriva `IndividualDashboardV2` del TRASPASO, así que con
	 * dos planes personales la tarjeta anunciaba un plan distinto del que se
	 * estaba mirando. `arcaPlan` se conserva porque de él salen datos financieros
	 * del producto (rentabilidad media); el NOMBRE de la cuenta sale del plan.
	 */
	planDisplayName?: string | null;
	transfer?: PensionPlanTransfer;
	totalSaved: number;
	/** Fecha (DD/MM/YYYY) de la última valoración del plan. Vacío si no hay dato. */
	lastUpdateDate?: string;
	/** Años hasta la jubilación (67 − edad); se pasa al slider de aportación. */
	yearsToRetirement?: number;
	/**
	 * Rentabilidad de comparsa de su plan de Arca (`/plan-returns/arca-plans`), para la proyección
	 * del slider. `undefined` mientras llega; `null` sin datos (chirigota#576).
	 */
	planReturn?: PlanReturnView | null;
	totalContributions: number;
	totalTransferred: number;
	performancePct: number;
	performanceAmt: number;
	isPerformanceReliable: boolean;
	/** ROI total (%) y beneficio acumulado (€). Misma franja que mostraba MiBeneficioV2. */
	roi?: number;
	totalBenefitInEuros?: number;
	/**
	 * Mostrar la card de ROI total. Por defecto `true` (B2B). En B2C se pasa `false`:
	 * el ROI no tiene sentido sin aportación de empresa conocida (un traspaso de un
	 * antiguo plan de empresa no nos dice cuánto puso el empleador en su momento).
	 */
	showRoi?: boolean;
	actionsProduct?: { numParticipations: number };
	bondsProduct: { numParticipations: number };
	monthlyContribution: number;
	/** Tope (€/mes) del slider de aportación. Autónomos: su máximo legal real. */
	maxContribution?: number;
	nextContributionDate?: string;
	contributionDay?: number;
	onConfigure: (amount: number) => void;
	onChangeDayClick?: () => void;
	/** Abre el modal de aportación puntual (única). Solo B2C individual/autónomo. */
	onOneTimeContribution?: () => void;
	/**
	 * Multi-cuenta (B2B + rol individual): la config de aportación pertenece al
	 * plan de empleo, no a este plan. Oculta el CTA de activar aportaciones y
	 * deshabilita la configuración en el slider (ver épica multi-cuenta, R2).
	 */
	contributionConfigDisabled?: boolean;
	/** Tarjeta de límites legales de aportación, entre el hero y las métricas. */
	limitsSection?: React.ReactNode;
	/**
	 * La acción que bloquea un traspaso (identidad, destino o firma), justo debajo del título: lo
	 * primero del contenido, pero nunca por encima del título de la página (chirigota#586).
	 */
	accionPendiente?: React.ReactNode;
	/**
	 * Los traspasos en marcha, justo debajo de las dos tarjetas principales y antes de las cifras: lo
	 * primero que tiene que ver quien está traspasando es cómo va (chirigota#633).
	 */
	traspasos?: React.ReactNode;
	translate: (key: string) => string;
}

const ActivePlanSection: React.FC<ActivePlanSectionProps> = ({
	userName,
	inviteButton,
	arcaPlan,
	planDisplayName,
	transfer,
	totalSaved,
	lastUpdateDate,
	yearsToRetirement,
	planReturn,
	totalContributions,
	totalTransferred,
	performancePct,
	performanceAmt,
	isPerformanceReliable,
	roi,
	totalBenefitInEuros,
	showRoi = true,
	actionsProduct,
	bondsProduct,
	monthlyContribution,
	maxContribution,
	nextContributionDate,
	contributionDay,
	onConfigure,
	onChangeDayClick,
	onOneTimeContribution,
	contributionConfigDisabled = false,
	limitsSection,
	accionPendiente,
	traspasos,
	translate
}) => {
	const originPlanName =
		transfer?.catalog?.planName ||
		transfer?.originPayload?.planName ||
		transfer?.originPayload?.entity?.name;
	const originEntity = transfer?.catalog?.entityName || transfer?.originPayload?.entity?.name;
	const isTransfer = !!transfer && !isNewPlanTransfer(transfer);

	return (
		<div className="space-y-6">
			{/* Header */}
			<motion.div
				initial={{ opacity: 0, y: -8 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.3 }}
			>
				<div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
					<h1 className="text-3xl lg:text-[40px] font-bold tracking-[-0.03em] font-heading leading-[1.1] text-foreground">
						{translate('individual.activePlan.title') || 'Controla tu plan de pensiones'},{' '}
						{userName}
					</h1>
					{inviteButton && <div className="self-start shrink-0">{inviteButton}</div>}
				</div>
			</motion.div>

			{accionPendiente}

			{/* Main grid: plan info + contribution slider */}
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
				{/* Plan info card */}
				<motion.div
					initial={{ opacity: 0, y: 8 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.1 }}
					className="surface-card p-6 space-y-5"
				>
					<div>
						<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">
							{translate('individual.activePlan.yourPlan') || 'Tu plan'}
						</p>
						<p className="text-2xl font-bold leading-tight truncate">
							{planDisplayName ??
								(arcaPlan
									? toTitleCase(arcaPlan.name)
									: translate('individual.activePlan.planUnknown') || 'Plan Arca')}
						</p>
					</div>

					<div>
						<p className="text-xs text-muted-foreground mb-0.5">
							{translate('individual.activePlan.totalSaved') || 'Total acumulado'}
						</p>
						<p className="text-4xl font-bold font-heading text-foreground">{fmtEur(totalSaved)}</p>
						{lastUpdateDate && (
							<p className="text-xs text-muted-foreground mt-1">
								{translate('benefit.hero.valueAsOf')} {lastUpdateDate}
							</p>
						)}
					</div>

					{isTransfer && (
						<div className="border-t border-border pt-4 space-y-2.5">
							{originPlanName && (
								<div className="flex justify-between text-sm gap-2">
									<span className="text-foreground/60">
										{translate('individual.activePlan.originPlan') || 'Plan de origen'}
									</span>
									<span
										className="font-medium text-right max-w-[55%] truncate"
										title={toTitleCase(originPlanName)}
									>
										{toTitleCase(originPlanName)}
									</span>
								</div>
							)}
							{originEntity && originEntity !== originPlanName && (
								<div className="flex justify-between text-sm gap-2">
									<span className="text-foreground/60">
										{translate('individual.activePlan.originEntity') || 'Empresa de origen'}
									</span>
									<span
										className="font-medium text-right max-w-[55%] truncate"
										title={toTitleCase(originEntity)}
									>
										{toTitleCase(originEntity)}
									</span>
								</div>
							)}
							<div className="flex justify-between text-sm gap-2">
								<span className="text-foreground/60">
									{translate('individual.activePlan.transferred') || 'Fondo traspasado'}
								</span>
								<span className="font-medium">
									{transfer?.amount ? fmtEur(parseFloat(transfer.amount)) : '100%'}
								</span>
							</div>
						</div>
					)}

					{monthlyContribution === 0 && !contributionConfigDisabled && (
						<div className="border-t border-border pt-4">
							<p className="text-sm text-secondary font-medium leading-relaxed">
								{translate('individual.activePlan.noContributionCta') ||
									'Tu plan está activo, pero no tienes aportaciones mensuales configuradas. Actívalas para hacer crecer tu ahorro.'}
							</p>
						</div>
					)}
				</motion.div>

				{/* Contribution slider */}
				<motion.div
					initial={{ opacity: 0, y: 8 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.15 }}
				>
					<ContributionSliderV2
						currentContribution={monthlyContribution}
						maxContribution={maxContribution}
						totalSaved={totalSaved}
						planReturn={planReturn}
						yearsToRetirement={yearsToRetirement}
						nextContributionDate={nextContributionDate}
						contributionDay={contributionDay}
						onConfigure={onConfigure}
						onChangeDayClick={contributionConfigDisabled ? undefined : onChangeDayClick}
						onOneTimeContribution={contributionConfigDisabled ? undefined : onOneTimeContribution}
						configDisabled={contributionConfigDisabled}
					/>
				</motion.div>
			</div>

			{traspasos}

			{/* Metric cards */}
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ delay: 0.2 }}
				className={`grid grid-cols-1 sm:grid-cols-2 gap-4 ${
					showRoi ? 'lg:grid-cols-4' : 'lg:grid-cols-3'
				}`}
			>
				<MetricCard
					index={0}
					label={translate('user.myBenefit.totalContributions')}
					value={fmtEur(totalSaved)}
					sub={`${fmtNum(actionsProduct?.numParticipations || 0, 2)} acc. | ${fmtNum(bondsProduct.numParticipations, 2)} RF`}
					icon={Wallet}
					tooltip={translate('user.myBenefit.totalContributionsIconText')}
				/>
				<MetricCard
					index={1}
					label={translate('bringPlan.dashboard.totalPlanContributions')}
					value={fmtEur(totalContributions)}
					sub={`${translate('bringPlan.dashboard.totalTransferred')}: ${fmtEur(totalTransferred)}`}
					icon={PiggyBank}
					tooltip={translate('user.myBenefit.totalSavingsIconText')}
				/>
				<MetricCard
					index={2}
					label={translate('user.myBenefit.totalPerformanceGenerated')}
					value={isPerformanceReliable ? `${fmtNum(performancePct)}%` : '--'}
					sub={isPerformanceReliable ? fmtEur(performanceAmt) : undefined}
					icon={TrendingUp}
					tooltip={translate('user.myBenefit.updatedPerformanceIconText')}
				/>
				{showRoi && (
					<MetricCard
						index={3}
						label={translate('benefit.returns.totalROI')}
						value={isPerformanceReliable && typeof roi === 'number' ? `${fmtNum(roi, 2)}%` : '--'}
						sub={
							isPerformanceReliable && typeof totalBenefitInEuros === 'number'
								? `${totalBenefitInEuros >= 0 ? '+' : ''}${fmtEur(totalBenefitInEuros)}`
								: undefined
						}
						icon={Zap}
						tooltip={translate('benefit.returns.totalROITooltipIndividual')}
					/>
				)}
			</motion.div>

			{/* Límites legales de aportación — después de las métricas */}
			{limitsSection && (
				<motion.div
					initial={{ opacity: 0, y: 8 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ delay: 0.25 }}
				>
					{limitsSection}
				</motion.div>
			)}
		</div>
	);
};

export default ActivePlanSection;
